/**
 * Domain login / session gates.
 * Geo for exodopanel.com (Indonesia only) is ops-side (nginx GeoIP on DO or Cloudflare) — not enforced here.
 */

export const AFFLAND_ADMIN_ALLOWLIST_IDS = [263, 321, 346];

export const PARTNER_ROLE_ID = 4;
export const ADVERTISER_ROLE_ID = 11;

export function normalizeHostname(host) {
  if (!host) return '';
  return String(host).split(':')[0].toLowerCase();
}

export function isExodoPanelHost(hostname) {
  const host = normalizeHostname(hostname);
  return host === 'exodopanel.com' || host === 'staging.exodopanel.com';
}

export function isAfflandPartnerHost(hostname) {
  return normalizeHostname(hostname) === 'partner.affland.id';
}

export function isAdvertiserHost(hostname) {
  return normalizeHostname(hostname) === 'advertiser.exodopanel.com';
}

/** LeadScale portal: production app + temporary advertiser test host. */
export function isLeadScaleHost(hostname) {
  const host = normalizeHostname(hostname);
  return host === 'app.leadscaleglobal.com' || host === 'advertiser.exodopanel.com';
}

/** Persist on users.register_source at self-registration. */
export function resolveRegisterSource(hostname, roleId) {
  const host = normalizeHostname(hostname);
  if (isLeadScaleHost(host) || Number(roleId) === ADVERTISER_ROLE_ID) {
    return 'leadscale';
  }
  if (host === 'partner.affland.id' || host === 'affland.id' || Number(roleId) === PARTNER_ROLE_ID) {
    return 'affland';
  }
  return 'exodo';
}

/** Human label for UI badges. */
export function registerSourceLabel(source) {
  switch (String(source || '').toLowerCase()) {
    case 'leadscale':
      return 'LeadScale';
    case 'affland':
      return 'Affland';
    case 'exodo':
      return 'Exodo';
    default:
      return null;
  }
}

export function isLocalDevHost(hostname) {
  const host = normalizeHostname(hostname);
  return host === 'localhost' || host === '127.0.0.1';
}

/** Collect numeric role ids from id_role + roles array / roleIds. */
export function collectRoleIds({ id_role, roleIds, roles } = {}) {
  const ids = new Set();
  if (id_role != null && id_role !== '') {
    ids.add(Number(id_role));
  }
  if (Array.isArray(roleIds)) {
    roleIds.forEach((id) => ids.add(Number(id)));
  } else if (typeof roleIds === 'string' && roleIds.length) {
    roleIds.split(',').forEach((id) => {
      if (id !== '') ids.add(Number(id));
    });
  }
  if (Array.isArray(roles)) {
    roles.forEach((r) => {
      if (r?.id != null) ids.add(Number(r.id));
    });
  }
  return [...ids].filter((n) => !Number.isNaN(n));
}

export function isPartnerOrAdvertiserRole(roleIds) {
  const ids = Array.isArray(roleIds) ? roleIds.map(Number) : collectRoleIds({ roleIds });
  return ids.includes(PARTNER_ROLE_ID) || ids.includes(ADVERTISER_ROLE_ID);
}

export function isAfflandAdminAllowlisted(userId) {
  return AFFLAND_ADMIN_ALLOWLIST_IDS.includes(Number(userId));
}

/** partner.affland.id: role 4 or allowlisted admin user ids (not advertisers). */
export function isAfflandAllowedUser({ id, id_role, roleIds, roles } = {}) {
  if (isAfflandAdminAllowlisted(id)) return true;
  const ids = collectRoleIds({ id_role, roleIds, roles });
  return ids.includes(PARTNER_ROLE_ID);
}

/** Legacy: role 11 only (kept for callers; LeadScale portal uses isLeadScaleAllowedUser). */
export function isAdvertiserAllowedUser({ id_role, roleIds, roles } = {}) {
  const ids = collectRoleIds({ id_role, roleIds, roles });
  return ids.includes(ADVERTISER_ROLE_ID);
}

/** app.leadscaleglobal.com + advertiser.exodopanel.com: partner (4) or advertiser (11). */
export function isLeadScaleAllowedUser({ id_role, roleIds, roles } = {}) {
  const ids = collectRoleIds({ id_role, roleIds, roles });
  return ids.includes(PARTNER_ROLE_ID) || ids.includes(ADVERTISER_ROLE_ID);
}

/**
 * Session user should live on LeadScale host (dark lime UI):
 * role 11, or role 4 with register_source=leadscale.
 */
export function isLeadScalePortalSessionUser(user = {}) {
  const role = Number(user?.id_role ?? user?.role?.id);
  if (role === ADVERTISER_ROLE_ID) return true;
  if (role !== PARTNER_ROLE_ID) return false;
  return String(user?.register_source || '').toLowerCase() === 'leadscale';
}
